import { TFunction } from 'i18next';
import { AccountAccessError } from 'src/api/auth.service';

export type ContactKind = 'email' | 'phone';
export type Contact = { kind: ContactKind; value: string };
export type AccessSession = {
    session: string;
    contact: Contact;
    /** Seconds the code stays valid. */
    expiresIn?: number;
    deliveryFailed?: boolean;
};
export type AccessTokens = {
    accessToken: string;
    refreshToken: string;
    tokenEndpoint: string;
    createUser?: boolean;
};

/** Shows +4791234567 as +47 912 34 567. */
export const formatContact = ({ kind, value }: Contact): string =>
    kind === 'phone'
        ? value.replace(/^\+47(\d{3})(\d{2})(\d{3})$/, '+47 $1 $2 $3')
        : value;

/** Shows a wait or a code lifetime in the largest unit that reads naturally. */
export const formatDuration = (seconds: number, t: TFunction): string => {
    const minutes = Math.max(1, Math.ceil(seconds / 60));
    if (minutes <= 90)
        return t('accountAccess.duration.minutes', { count: minutes });
    const hours = Math.ceil(minutes / 60);
    if (hours < 48) return t('accountAccess.duration.hours', { count: hours });
    return t('accountAccess.duration.days', { count: Math.ceil(hours / 24) });
};

export const accessErrorCode = (error: unknown): string | undefined => {
    if (error instanceof AccountAccessError) return error.code;
    // The API client keeps the server's code on other request errors too.
    const code = (error as { code?: unknown } | undefined)?.code;
    return typeof code === 'string' ? code : undefined;
};

/** Translates the stable codes returned by the account-access endpoints. */
export const accessErrorMessage = (error: unknown, t: TFunction): string => {
    const code = accessErrorCode(error);
    switch (code) {
        case 'RATE_LIMITED': {
            const retryAfter = (error as { retryAfter?: number }).retryAfter;
            return t('accountAccess.errors.rateLimited', {
                duration: formatDuration(retryAfter ?? 60, t),
            });
        }
        case 'CODE_INVALID':
        case 'CODE_EXPIRED':
        case 'CONTACT_EXISTS':
        case 'CONTACT_CONFIRMED':
        case 'CONTACT_NOT_VERIFIED':
        case 'CONTACT_NOT_FOUND':
        case 'ACCOUNT_ACTIVE':
        case 'LAST_INVITATION_CONTACT':
        case 'SIGN_IN_REQUIRED':
        case 'SESSION_EXPIRED':
        case 'ACTIVATION_REQUIRED':
            return t(`accountAccess.errors.${code}`);
        default:
            return error instanceof Error
                ? error.message
                : t('errors.unknownLogin');
    }
};
